/**
 * Sysadmin Tenant Detail → Hardware tab (EP-1 §8.3).
 *
 * Per docs/cross-cutting/EP-1-hardware-brand-slo-matrix.md §8.3:
 *   - Current profile card (display name, vendor, accelerator, vram, ram)
 *   - Assignment history table (every row from
 *     `tenant_hardware_assignments`, time-versioned via valid_from/valid_to)
 *   - "Change profile" button → confirm dialog → PUT
 *     /admin/platform/tenants/:id/hardware
 *   - Per-brand verdict matrix: for each brand currently assigned to
 *     the tenant, shows the preflight verdict on this profile
 *
 * EP-1 §8 acceptance criterion #2 lives partly here: the verdict
 * matrix slice per tenant. The matrix on /sysadmin/models §8.2 covers
 * the cross-tenant axis; this view covers the per-tenant axis.
 *
 * Tab IS sysadmin-only (per `feedback_ui_parity_sysadmin_tenant`,
 * tenants are read-only for hardware assignments — no tenant-scope
 * mirror of this surface).
 */

import { useEffect, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Cpu,
  ArrowRight,
  HardDrive,
  Network as NetworkIcon,
  Server,
  History,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  Pencil,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SectionCard } from '@/components/ui/section-card';
import { StatTile } from '@/components/ui/stat-tile';
import { toast } from 'sonner';
import {
  useTenantHardware,
  useUpdateTenantHardware,
  useHardwareProfiles,
  usePreflightAssignment,
  type PreflightVerdict,
} from '@/lib/use-sysadmin-ep1';
import { useArgusAssignments } from '@/lib/use-argus-assignments';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/hardware')({
  component: TenantHardwareTab,
});

function TenantHardwareTab() {
  const { tenantId } = useParams({
    from: '/sysadmin/tenants/$tenantId/hardware',
  });

  const hw = useTenantHardware(tenantId);
  const profiles = useHardwareProfiles();
  const assignments = useArgusAssignments(tenantId);

  const [changeOpen, setChangeOpen] = useState(false);

  if (hw.isLoading) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold tracking-tight">Hardware</h1>
        <div className="text-sm text-muted-foreground animate-pulse">
          Loading hardware profile…
        </div>
      </div>
    );
  }
  if (hw.error) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold tracking-tight">Hardware</h1>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load hardware</AlertTitle>
          <AlertDescription>{hw.error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const current = hw.data?.current ?? null;
  const history = hw.data?.history ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Cpu className="h-5 w-5 text-muted-foreground" />
            Hardware
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            The hardware substrate this tenant runs on. Brand assignments
            are validated against this profile via preflight before
            isEnabled writes — see{' '}
            <span className="font-medium text-foreground">EP-1 §8.3</span>.
          </p>
        </div>
        <Button onClick={() => setChangeOpen(true)} variant="outline" size="sm">
          <Pencil className="h-4 w-4 mr-2" /> Change profile
        </Button>
      </div>

      {/* ─── Current profile card ───────────────────────────────────── */}
      <SectionCard
        title="Current profile"
        icon={Server}
        description={
          current
            ? `Active since ${formatDate(current.assignment.validFrom)}`
            : 'No active hardware profile — assign one below'
        }
      >
        {current ? (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-semibold">
                  {current.profile.displayName}
                </div>
                <div className="text-xs font-mono text-muted-foreground mt-0.5">
                  {current.profile.slug}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className="text-[10px]">
                  {current.profile.vendor.toUpperCase()}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {current.profile.acceleratorModel}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {current.profile.networkIsolationTier}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile
                label="VRAM"
                value={`${current.profile.vramGb} GB`}
                icon={HardDrive}
              />
              <StatTile
                label="System RAM"
                value={`${current.profile.systemRamGb} GB`}
                icon={Server}
              />
              <StatTile
                label="Vendor"
                value={current.profile.vendor.toUpperCase()}
                icon={Cpu}
              />
              <StatTile
                label="Network"
                value={networkLabel(current.profile.networkIsolationTier)}
                icon={NetworkIcon}
              />
            </div>
          </div>
        ) : (
          <div className="py-4 text-sm text-muted-foreground text-center">
            No hardware profile assigned. Click "Change profile" above to
            pick one.
          </div>
        )}
      </SectionCard>

      {/* ─── What this means: per-brand verdict matrix ──────────────── */}
      <SectionCard
        title="What this means — brand compatibility on this profile"
        icon={CheckCircle2}
        description="Preflight verdict per currently-assigned brand. RED rows will time out on this hardware unless risk-accepted."
      >
        <BrandVerdictMatrix
          tenantId={tenantId}
          hasProfile={current !== null}
          assignments={assignments.data?.items ?? []}
          isLoading={assignments.isLoading}
          error={assignments.error}
        />
      </SectionCard>

      {/* ─── Assignment history ─────────────────────────────────────── */}
      <SectionCard
        title="Assignment history"
        icon={History}
        description={`${history.length} record${history.length === 1 ? '' : 's'}`}
      >
        {history.length === 0 ? (
          <div className="py-3 text-sm text-muted-foreground text-center">
            No hardware-profile history yet for this tenant.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Profile</TableHead>
                <TableHead>Valid from</TableHead>
                <TableHead>Valid to</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>
                    <div className="text-sm font-medium">
                      {h.profile.displayName}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground">
                      {h.profile.slug}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">
                    {formatDate(h.validFrom)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {h.validTo ? (
                      formatDate(h.validTo)
                    ) : (
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400">
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md">
                    {h.notes ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <ChangeProfileDialog
        tenantId={tenantId}
        open={changeOpen}
        onOpenChange={setChangeOpen}
        currentProfileId={current?.profile.id ?? null}
        profiles={profiles.data?.profiles ?? []}
        isLoadingProfiles={profiles.isLoading}
      />
    </div>
  );
}

// ─── Per-brand verdict matrix ───────────────────────────────────────────

function BrandVerdictMatrix({
  tenantId,
  hasProfile,
  assignments,
  isLoading,
  error,
}: {
  tenantId: string;
  hasProfile: boolean;
  assignments: Array<{
    id: string;
    argusModelId: string;
    brandId: string;
    isEnabled: boolean;
  }>;
  isLoading: boolean;
  error: Error | null;
}) {
  if (!hasProfile) {
    return (
      <div className="py-3 text-sm text-muted-foreground text-center">
        Assign a hardware profile above to see compatibility verdicts.
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="py-3 text-sm text-muted-foreground text-center animate-pulse">
        Loading brand assignments…
      </div>
    );
  }
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to load brand assignments: {error.message}
        </AlertDescription>
      </Alert>
    );
  }
  if (assignments.length === 0) {
    return (
      <div className="py-3 text-sm text-muted-foreground text-center">
        No brand assignments for this tenant yet.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Brand</TableHead>
          <TableHead>Enabled</TableHead>
          <TableHead>Verdict</TableHead>
          <TableHead>p95 first-token</TableHead>
          <TableHead>Reasoning</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {assignments.map((a) => (
          <BrandVerdictRow
            key={a.id}
            tenantId={tenantId}
            argusModelId={a.argusModelId}
            brandId={a.brandId}
            isEnabled={a.isEnabled}
          />
        ))}
      </TableBody>
    </Table>
  );
}

function BrandVerdictRow({
  tenantId,
  argusModelId,
  brandId,
  isEnabled,
}: {
  tenantId: string;
  argusModelId: string;
  brandId: string;
  isEnabled: boolean;
}) {
  const preflight = usePreflightAssignment(tenantId);
  const [evaluated, setEvaluated] = useState<{
    verdict: PreflightVerdict;
    reasoning: string[];
    projectedFirstTokenP95Ms: number | null;
    budgetMs: number;
  } | null>(null);

  const evaluate = async () => {
    try {
      const res = await preflight.mutateAsync({ brandId: argusModelId });
      setEvaluated({
        verdict: res.verdict,
        reasoning: res.reasoning,
        projectedFirstTokenP95Ms: res.projectedFirstTokenP95Ms,
        budgetMs: res.measuredHelperBudgetMs,
      });
    } catch (e) {
      toast.error('Preflight failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <TableRow>
      <TableCell>
        <div className="text-sm font-medium">{brandId}</div>
      </TableCell>
      <TableCell>
        {isEnabled ? (
          <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400">
            on
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]">
            off
          </Badge>
        )}
      </TableCell>
      <TableCell>
        {evaluated ? (
          <VerdictBadge verdict={evaluated.verdict} />
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={evaluate}
            disabled={preflight.isPending}
            className="h-7 text-xs"
          >
            <RefreshCw
              className={cn(
                'h-3 w-3 mr-1',
                preflight.isPending && 'animate-spin',
              )}
            />
            Evaluate
          </Button>
        )}
      </TableCell>
      <TableCell className="text-xs tabular-nums">
        {evaluated?.projectedFirstTokenP95Ms != null
          ? `${evaluated.projectedFirstTokenP95Ms} ms`
          : '—'}
        {evaluated && (
          <span className="text-muted-foreground">
            {' / '}budget {evaluated.budgetMs} ms
          </span>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground max-w-md">
        {evaluated?.reasoning?.length ? (
          <ul className="list-disc ml-4 space-y-0.5">
            {evaluated.reasoning.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        ) : (
          '—'
        )}
      </TableCell>
    </TableRow>
  );
}

// ─── Verdict badge (shared, also exported for the assignment dialog) ────

export function VerdictBadge({ verdict }: { verdict: PreflightVerdict }) {
  if (verdict === 'green') {
    return (
      <Badge
        className="text-[10px] gap-1 bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400"
        title="First-token p95 is comfortably under the helper budget"
      >
        <CheckCircle2 className="h-3 w-3" />
        GREEN
      </Badge>
    );
  }
  if (verdict === 'yellow') {
    return (
      <Badge
        className="text-[10px] gap-1 bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400"
        title="First-token p95 is within 70-150% of the helper budget; assignment will work but is at risk under load"
      >
        <AlertCircle className="h-3 w-3" />
        YELLOW
      </Badge>
    );
  }
  return (
    <Badge
      className="text-[10px] gap-1 bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400"
      title="First-token p95 exceeds helper budget by >50% — assignment will time out without risk-accept override"
    >
      <AlertTriangle className="h-3 w-3" />
      RED
    </Badge>
  );
}

// ─── Change profile dialog ──────────────────────────────────────────────

function ChangeProfileDialog({
  tenantId,
  open,
  onOpenChange,
  currentProfileId,
  profiles,
  isLoadingProfiles,
}: {
  tenantId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentProfileId: string | null;
  profiles: Array<{
    id: string;
    slug: string;
    displayName: string;
    vendor: string;
    acceleratorModel: string;
    vramGb: number;
    systemRamGb: number;
    deletedAt: string | null;
  }>;
  isLoadingProfiles: boolean;
}) {
  const update = useUpdateTenantHardware(tenantId);
  const [selected, setSelected] = useState<string>(currentProfileId ?? '');
  const [notes, setNotes] = useState('');

  // Reset when reopening so the dialog never carries stale state.
  useEffect(() => {
    if (open) {
      setSelected(currentProfileId ?? '');
      setNotes('');
    }
  }, [open, currentProfileId]);

  const eligibleProfiles = profiles.filter((p) => !p.deletedAt);
  const sameAsCurrent = selected === currentProfileId;
  const canSubmit = selected.length > 0 && !sameAsCurrent;

  const submit = async () => {
    try {
      await update.mutateAsync({
        hardwareProfileId: selected,
        notes: notes.trim() || undefined,
      });
      toast.success('Hardware profile updated');
      onOpenChange(false);
    } catch (e) {
      toast.error('Update failed', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change hardware profile</DialogTitle>
          <DialogDescription>
            Switching the profile re-validates every brand assignment for
            this tenant. RED assignments will require risk-accept overrides
            before they can be re-enabled.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase text-muted-foreground">
              New profile
            </span>
            <Select
              value={selected}
              onValueChange={setSelected}
              disabled={isLoadingProfiles}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isLoadingProfiles ? 'Loading…' : 'Select a profile'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {eligibleProfiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="font-medium">{p.displayName}</span>
                    <span className="text-muted-foreground">
                      {' '}
                      ({p.vramGb} GB VRAM)
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase text-muted-foreground">
              Notes (optional, audited)
            </span>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. customer upgraded to A6000 box, 2026-Q3 hardware refresh"
            />
          </label>

          {sameAsCurrent && selected !== '' && (
            <div className="text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded">
              This is the current profile. Pick a different one to swap.
            </div>
          )}
        </div>

        {update.error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{update.error.message}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit || update.isPending}
            onClick={submit}
            className="gap-2"
          >
            {update.isPending ? 'Updating…' : 'Change profile'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function networkLabel(tier: string): string {
  if (tier === 'no_egress') return 'Air-gap';
  if (tier === 'allowlist') return 'Allowlist';
  if (tier === 'open') return 'Open';
  return tier;
}
